import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { SelfServiceStateService } from './self-service-state.service';
import { SelfServiceDashboardService } from './self-service-dashboard.service';
import { SelfServiceUiService } from './self-service-ui.service';
import { SelfServiceDataUtilsService } from './self-service-data-utils.service';
import { writeJson } from './self-service-storage.utils';
import {
  AggregationType,
  ChartPreviewKind,
  ChartShapeOptions,
  ChartStyle,
  CustomAggregation,
  DashboardDraftItem,
  DashboardDraftState,
  DashboardStatus,
  DashboardTab,
  GlobalFilter,
  GlobalFiltersPosition,
  SavedSelfServiceChart,
  SavedSelfServiceDashboard,
  WidgetConfig,
  isWidgetPreviewKind,
} from './self-service-types';

/**
 * Shared published-card height (px) for the "standard" data cards — ECharts
 * charts, and the generic drill-down. Keeping these on one value makes those
 * cards line up in the published grid instead of each shrinking to its own
 * content. Matches the builder arrange-view `.dashboard-chart` (240px).
 */
const STANDARD_CARD_HEIGHT_PX = 240;
const DRILL_DOWN_CARD_HEIGHT_PX = STANDARD_CARD_HEIGHT_PX;

/**
 * Dashboard config-building + persistence cluster extracted from
 * SelfServiceVisualizationComponent. All cross-stage state is read from the
 * shared SelfServiceStateService (signals) rather than component fields.
 *
 * NOTE ON dependencies copied alongside the requested methods:
 * `buildWidgetEntry` / `buildPublishedDataset` (in the requested copy list)
 * genuinely depend on `dataUtils`, `customAggregations`, `builtinAggregations`,
 * `currentShapeOptions`, `getShapeOptionsFromChart`, and `toGanttDateString`.
 * These are carried into the service (injection + private fields/helpers) so the
 * copied bodies remain verbatim and the service is self-consistent.
 */
@Injectable({ providedIn: 'root' })
export class SelfServiceChartPersistenceService {
  private static readonly SAVED_CHARTS_STORAGE_KEY = 'dvm.selfService.savedCharts';
  private static readonly DASHBOARD_DRAFT_STORAGE_KEY = 'dvm.selfService.dashboardDraft';
  private static readonly SAVED_DASHBOARDS_STORAGE_KEY = 'dvm.selfService.savedDashboards';

  // Chart types whose components consume a { category, series:[{name,data}] }
  // shape. Kept in sync with CATEGORY_SERIES_CHART_TYPES in self-service-data.utils.ts.
  private static readonly CATEGORY_SERIES_CHART_TYPES = new Set<string>([
    'stack-bar-chart', 'generic-stack-bar-chart', 'stack-bar-chart-generic',
    'mixed-stackbar-chart', 'clustered-bar-chart', 'stacked-100-bar-chart',
    'stacked-line-chart', 'stacked-area-chart', 'combined-chart', 'bar-line',
    'mbar-mline', 'multi-level-comparison-chart',
  ]);
  // Bespoke shapes handled separately from the category-series union.
  private static readonly LINE_COLUMN_CHART_TYPES = new Set<string>(['line-column-chart']);
  private static readonly COMBO_CHART_TYPES = new Set<string>(['multi-series-bar-line-combo-chart']);
  // Reads { categories, series } but must NOT receive a `bar` field (which would
  // trigger its domain-specific cummins/non-cummins path).
  private static readonly HORIZONTAL_STACK_CHART_TYPES = new Set<string>([
    'stacked-horizontal-bar-chart', 'horizontal-bar-chart-segment',
  ]);
  // Plain waterfall: [Placeholder, Increase, Decrease] ECharts series.
  private static readonly WATERFALL_CHART_TYPES = new Set<string>(['waterfall-chart']);
  // Waterfall-stack renders generic { category, series:[{name,data}] }.
  private static readonly WATERFALL_STACK_CHART_TYPES = new Set<string>(['waterfall-stack']);

  // Carried from the component: builder-side aggregation definitions consumed by
  // buildPublishedDataset / buildWidgetEntry. Populated from the active builder
  // state via setCustomAggregations() before config rebuilds.
  private customAggregations: CustomAggregation[] = [];
  private readonly builtinAggregations: Array<{ label: string; value: AggregationType }> = [
    { label: 'Sum', value: 'sum' },
    { label: 'Average', value: 'avg' },
    { label: 'Count', value: 'count' },
    { label: 'Distinct Count', value: 'distinctCount' },
    { label: 'Distinct Values', value: 'distinct' },
    { label: 'Minimum', value: 'min' },
    { label: 'Maximum', value: 'max' },
    { label: 'Median', value: 'median' },
    { label: 'Range (max - min)', value: 'range' },
    { label: 'First', value: 'first' },
    { label: 'Last', value: 'last' },
  ];

  // Scratch field consulted by dataUtils.aggregateByCategory for sort/topN/
  // granularity. buildPublishedDataset sets/restores it per-chart.
  private currentShapeOptions: ChartShapeOptions | null = null;

  constructor(
    private readonly state: SelfServiceStateService,
    private readonly dashboardService: SelfServiceDashboardService,
    private readonly ui: SelfServiceUiService,
    private readonly dataUtils: SelfServiceDataUtilsService,
  ) {}

  /** Sync the builder-side custom aggregations so published datasets resolve
   *  custom aggregation keys the same way the live preview does. */
  setCustomAggregations(customAggregations: CustomAggregation[]): void {
    this.customAggregations = customAggregations;
  }

  /** Disposable self-service caches, most-disposable first, that a write may
   *  EVICT to reclaim quota (see writeJson's reclaimKeys). The dashboard draft is
   *  an in-progress autosave rebuilt from the live builder state on the next
   *  edit, so it is the first to go. savedDashboards is stripped of dataset rows
   *  (stripDatasetsFromDashboards) but still holds config metadata, so it is a
   *  last resort. savedCharts is NEVER offered here — its rows back the live
   *  preview/render and are not always re-fetchable. `exclude` drops the key
   *  currently being written so we never evict our own target. */
  private reclaimableKeys(exclude: string): string[] {
    return [
      SelfServiceChartPersistenceService.DASHBOARD_DRAFT_STORAGE_KEY,
      SelfServiceChartPersistenceService.SAVED_DASHBOARDS_STORAGE_KEY,
    ].filter((key) => key !== exclude);
  }

  persistSavedCharts(charts: SavedSelfServiceChart[]): boolean {
    if (writeJson(
      SelfServiceChartPersistenceService.SAVED_CHARTS_STORAGE_KEY,
      charts,
      this.reclaimableKeys(SelfServiceChartPersistenceService.SAVED_CHARTS_STORAGE_KEY),
    )) {
      return true;
    }
    this.ui.showSuccess('');
    this.ui.showError('Unable to save chart locally. The uploaded dataset may be too large for browser storage.');
    return false;
  }

  persistDashboardDraft(items: DashboardDraftItem[]): boolean {
    try {
      const draft: DashboardDraftState = {
        name: this.state.dashboardName().trim() || 'Untitled dashboard',
        items,
        globalFilters: this.state.dashboardGlobalFilters(),
        globalFiltersPosition: this.state.dashboardGlobalFiltersPosition(),
        layout: { columns: this.state.dashboardColumns() },
        ...(this.state.dashboardTabs().length ? { tabs: this.state.dashboardTabs() } : {}),
      };
      if (writeJson(SelfServiceChartPersistenceService.DASHBOARD_DRAFT_STORAGE_KEY, draft)) {
        return true;
      }
      this.ui.showError('Unable to save dashboard draft locally.');
      return false;
    } catch {
      this.ui.showError('Unable to save dashboard draft locally.');
      return false;
    }
  }

  persistSavedDashboards(dashboards: SavedSelfServiceDashboard[], options: { silent?: boolean } = {}): boolean {
    // The heavy part of each dashboard is its `config` blob, which embeds a FULL
    // copy of every chart's dataset rows (buildWidgetEntry sets
    // widget.selfService.rawRows + widget.data). Those exact rows already live
    // under the savedCharts key, so persisting them again here duplicates the
    // dataset and is what pushes large uploads past the ~5MB localStorage quota
    // (setItem then throws QuotaExceededError). Strip the rows from the LOCAL
    // copy before writing — no local reader needs them: opening a local
    // dashboard resolves charts from savedCharts by item.chartId, and "Back to
    // Builder" reconstructs from the API's full layout, not this stripped copy.
    // The server publish (dashboardService.saveDashboard) sends the full,
    // unstripped config, so published rendering is unaffected.
    const storable = this.stripDatasetsFromDashboards(dashboards);
    if (writeJson(
      SelfServiceChartPersistenceService.SAVED_DASHBOARDS_STORAGE_KEY,
      storable,
      this.reclaimableKeys(SelfServiceChartPersistenceService.SAVED_DASHBOARDS_STORAGE_KEY),
    )) {
      return true;
    }
    if (!options.silent) {
      this.ui.showSuccess('');
      this.ui.showError('Unable to save dashboard locally. The uploaded dataset may be too large for browser storage.');
    }
    return false;
  }

  /**
   * Return a storage-light copy of `dashboards` with the embedded dataset rows
   * removed from each dashboard's `config` (widget.selfService.rawRows and the
   * static widget.data fallback). The in-memory dashboards passed in are left
   * untouched — only the returned clone is trimmed, so callers keep the full
   * config for the current session while localStorage holds the light version.
   * `config` is always rebuilt from savedCharts + items (buildDashboardConfigFromItems)
   * whenever the full rows are actually needed, so nothing is lost at rest.
   */
  private stripDatasetsFromDashboards(
    dashboards: SavedSelfServiceDashboard[],
  ): SavedSelfServiceDashboard[] {
    return dashboards.map((dashboard) => {
      if (!dashboard.config || typeof dashboard.config !== 'object') {
        return dashboard;
      }
      return { ...dashboard, config: this.stripDatasetsFromConfig(dashboard.config) };
    });
  }

  /** Deep-clone a dashboard `config` and drop the per-widget dataset rows.
   *  Walks both single-page (`pageContent`) and tabbed (`tabs[].pageContent`)
   *  shapes emitted by buildDashboardConfigFromItems. */
  private stripDatasetsFromConfig(
    config: Record<string, unknown>,
  ): Record<string, unknown> {
    const next: Record<string, unknown> = { ...config };

    if (Array.isArray(next['pageContent'])) {
      next['pageContent'] = this.stripDatasetsFromPageContent(next['pageContent']);
    }

    if (Array.isArray(next['tabs'])) {
      next['tabs'] = (next['tabs'] as unknown[]).map((tab) => {
        if (!tab || typeof tab !== 'object') return tab;
        const tabRecord = tab as Record<string, unknown>;
        if (!Array.isArray(tabRecord['pageContent'])) return tab;
        return {
          ...tabRecord,
          pageContent: this.stripDatasetsFromPageContent(tabRecord['pageContent']),
        };
      });
    }

    return next;
  }

  /** Strip dataset rows from every widget in a `pageContent` array of
   *  `{ id, columns: widget[] }` rows. */
  private stripDatasetsFromPageContent(pageContent: unknown[]): unknown[] {
    return pageContent.map((row) => {
      if (!row || typeof row !== 'object') return row;
      const rowRecord = row as Record<string, unknown>;
      if (!Array.isArray(rowRecord['columns'])) return row;
      return {
        ...rowRecord,
        columns: (rowRecord['columns'] as unknown[]).map((widget) => this.stripWidgetDataset(widget)),
      };
    });
  }

  /** Remove the heavy dataset payload from a single widget: the raw rows under
   *  `selfService.rawRows`, the static `data` fallback, and — for interactive
   *  widgets (editable-table, configurable-form, upload-file, …) —
   *  `computationRows`/`computationColumns` (buildWidgetEntry's isWidget branch
   *  embeds the FULL dataset there too; left unstripped, a dashboard with even
   *  one such widget on a large dataset still exhausts the quota after all the
   *  other mitigations). Everything else (mapping, aggregation params, style,
   *  filters) is preserved so the light config still describes the widget; rows
   *  are re-derived on demand (local: resolved from savedCharts by chartId;
   *  published: reconstructDashboardFromRendered reads the API's full layout). */
  private stripWidgetDataset(widget: unknown): unknown {
    if (!widget || typeof widget !== 'object') return widget;
    const widgetRecord = widget as Record<string, unknown>;
    const next: Record<string, unknown> = { ...widgetRecord };

    if (next['selfService'] && typeof next['selfService'] === 'object') {
      const selfService = { ...(next['selfService'] as Record<string, unknown>) };
      delete selfService['rawRows'];
      next['selfService'] = selfService;
    }

    // `data` is only a static fallback for tooling that doesn't run the
    // selfService pipeline; live published rendering recomputes it. Dropping it
    // locally saves the second full copy of the aggregated rows.
    if ('data' in next) {
      delete next['data'];
    }

    // Interactive widgets carry the same dataset a third time under these keys
    // (computationEntityName is just a name, kept).
    if ('computationRows' in next) {
      next['computationRows'] = [];
    }
    if ('computationColumns' in next) {
      next['computationColumns'] = [];
    }

    return next;
  }

  buildDashboardConfig(): Record<string, unknown> {
    return this.buildDashboardConfigFromItems(
      this.state.dashboardName(),
      this.state.dashboardItems(),
      this.clampColumns(this.state.dashboardColumns()),
      this.state.dashboardStatus(),
      this.state.dashboardPublishedAt(),
      this.state.dashboardGlobalFilters(),
      this.state.dashboardGlobalFiltersPosition(),
      this.state.dashboardTabs(),
    );
  }

  /**
   * Build the grid `pageContent` (array of `{ id, columns }` rows) for a set of
   * items. `rowItems` are the items to place in these rows; `allItems` is the
   * FULL dashboard item list, passed to buildWidgetEntry so cross-filter
   * listener wiring + widgetId uniqueness resolve across the whole dashboard
   * (not just the current tab). Extracted so it can run once per tab.
   */
  private buildPageContent(
    rowItems: DashboardDraftItem[],
    allItems: DashboardDraftItem[],
    columns: number,
  ): Record<string, unknown>[] {
    const widgets: Array<{ widget: Record<string, unknown>; span: number }> = [];

    rowItems.forEach((item) => {
      const chart = this.resolveDashboardChart(item);
      if (!chart) {
        return;
      }
      const span = Math.min(
        this.clampSpan(item.colSpan ?? this.defaultSpanForChart(chart)),
        columns,
      );
      const widget = this.buildWidgetEntry(chart, item, allItems);
      widget['layout'] = { colSpan: span };
      widget['class'] = this.spanToBootstrapClass(span, columns);
      widgets.push({ widget, span });
    });

    const pageContent: Record<string, unknown>[] = [];
    let buffer: Record<string, unknown>[] = [];
    let bufferUsed = 0;
    for (const { widget, span } of widgets) {
      if (bufferUsed + span > columns && buffer.length) {
        pageContent.push({ id: `row${pageContent.length + 1}`, columns: buffer });
        buffer = [];
        bufferUsed = 0;
      }
      buffer.push(widget);
      bufferUsed += span;
    }
    if (buffer.length) {
      pageContent.push({ id: `row${pageContent.length + 1}`, columns: buffer });
    }

    return pageContent;
  }

  /** Published `filters` block from a set of GlobalFilters, or undefined when
   *  there are none. `optionsItems` scopes which datasets are scanned for
   *  dropdown values (the whole dashboard, so options are complete). */
  private buildFiltersBlock(
    filters: GlobalFilter[],
    position: GlobalFiltersPosition,
    optionsItems: DashboardDraftItem[],
  ): Record<string, unknown> | undefined {
    if (!filters.length) return undefined;
    return {
      position,
      content: filters.map((filter) => ({
        name: filter.label,
        key: filter.key,
        options: filter.key,
        optionsList: this.computePublishedFilterOptions(filter, optionsItems),
        type: filter.parentKey ? 'cascading-dropdown' : 'dropdown',
        isMultiSelect: filter.isMultiSelect,
        ...(filter.parentKey ? { parentKey: filter.parentKey } : {}),
      })),
    };
  }

  buildDashboardConfigFromItems(
    dashboardName: string,
    items: DashboardDraftItem[],
    columns: number,
    status: DashboardStatus,
    publishedAt: string | null,
    globalFilters: GlobalFilter[],
    globalFiltersPosition: GlobalFiltersPosition,
    tabs: DashboardTab[] = [],
  ): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      'dashboard-name': dashboardName.trim() || 'Untitled dashboard',
      screenFloating: true,
      enableEdit: true,
      layout: { columns },
      status: status,
      ...(publishedAt ? { publishedAt: publishedAt } : {}),
    };

    if (tabs.length) {
      // Tabbed dashboard: one pageContent per tab, from the items assigned to it.
      // The full `items` list is still passed to buildPageContent (as allItems)
      // for cross-dashboard listener/id resolution.
      payload['tabs'] = tabs.map((tab) => {
        const tabItems = items.filter((item) => item.tabId === tab.id);
        const tabConfig: Record<string, unknown> = {
          id: tab.id,
          label: tab.label,
          pageContent: this.buildPageContent(tabItems, items, columns),
        };
        const localFilters = this.buildFiltersBlock(tab.filters ?? [], 'top', items);
        if (localFilters) tabConfig['filters'] = localFilters;
        return tabConfig;
      });
    } else {
      // Single-page dashboard (unchanged behavior).
      payload['pageContent'] = this.buildPageContent(items, items, columns);
    }

    // Dashboard-level (global) filters apply across all tabs.
    const filtersBlock = this.buildFiltersBlock(globalFilters, globalFiltersPosition, items);
    if (filtersBlock) {
      payload['filters'] = filtersBlock;
    }

    return payload;
  }

  /**
   * Reverse of buildDashboardConfigFromItems: reconstruct an EDITABLE dashboard
   * (+ its charts) from ANY published `layout_json`, so "Back to Builder" can
   * make a published dashboard editable again even when there's no local copy
   * (opened from the sidebar / after logout / another machine). Works for OLD
   * and NEW dashboards alike because it reads only the RENDERED layout that
   * every published dashboard already carries — no special embedding required.
   *
   * Each widget produced by buildWidgetEntry carries its raw dataset +
   * aggregation/mapping params under `selfService` (data charts) or
   * `widgetConfig` + `computation*` (interactive widgets), plus `style`,
   * `filterKey`, `title` on the widget. We walk `pageContent` (single-page) or
   * `tabs[].pageContent` (tabbed) and rebuild a SavedSelfServiceChart +
   * DashboardDraftItem per widget.
   *
   * Best-effort + slightly lossy: builder-only settings NOT baked into the
   * rendered widget (custom computed columns, custom aggregation expressions)
   * can't be recovered; everything the widget carries is. Returns null when no
   * widget resolves (nothing editable to open).
   */
  reconstructDashboardFromRendered(
    config: Record<string, unknown> | null | undefined,
    fallbackId: string,
  ): { dashboard: SavedSelfServiceDashboard; charts: SavedSelfServiceChart[] } | null {
    if (!config || typeof config !== 'object') return null;
    const cfg = config as any;
    const name = String(cfg['dashboard-name'] ?? cfg['name'] ?? '').trim();
    if (!name) return null;

    const columns = this.clampColumns(cfg.layout?.columns);
    const items: DashboardDraftItem[] = [];
    const charts: SavedSelfServiceChart[] = [];

    const widgetEntries: Array<{ widget: any; tabId?: string }> = [];
    const collect = (pageContent: any, tabId?: string): void => {
      if (!Array.isArray(pageContent)) return;
      for (const row of pageContent) {
        const cols = Array.isArray(row?.columns) ? row.columns : [];
        for (const widget of cols) {
          if (widget && typeof widget === 'object') widgetEntries.push({ widget, tabId });
        }
      }
    };

    const tabs: DashboardTab[] = [];
    if (Array.isArray(cfg.tabs) && cfg.tabs.length) {
      for (const tab of cfg.tabs) {
        const tabId = String(tab?.id ?? `tab-${tabs.length + 1}`);
        tabs.push({ id: tabId, label: String(tab?.label ?? `Tab ${tabs.length + 1}`) });
        collect(tab?.pageContent, tabId);
      }
    } else {
      collect(cfg.pageContent);
    }

    let index = 0;
    for (const { widget, tabId } of widgetEntries) {
      const chart = this.chartFromRenderedWidget(widget, index);
      if (!chart) continue;
      items.push({
        id: `item-${index}-${chart.id}`,
        chartId: chart.id,
        addedAt: chart.createdAt,
        colSpan: this.clampSpan(widget?.layout?.colSpan),
        ...(widget?.filterKey ? { filterKeyOverride: String(widget.filterKey) } : {}),
        ...(tabId ? { tabId } : {}),
      });
      charts.push(chart);
      index++;
    }

    if (items.length === 0) return null;

    const now = new Date().toISOString();
    const dashboard: SavedSelfServiceDashboard = {
      id: fallbackId || `api:${this.normalizeDashboardName(name).replace(/\s+/g, '-')}`,
      name,
      createdAt: now,
      updatedAt: now,
      items,
      globalFilters: this.filtersFromRendered(cfg.filters),
      globalFiltersPosition: this.normalizeFiltersPosition(cfg.filters?.position),
      layout: { columns },
      status: cfg.status === 'draft' ? 'draft' : 'published',
      publishedAt: cfg.publishedAt || now,
      ...(tabs.length ? { tabs } : {}),
      config,
    };

    return { dashboard, charts };
  }

  /** Rebuild one SavedSelfServiceChart from a rendered widget entry. Reads the
   *  raw dataset + params off `selfService` (data charts) or `widgetConfig`
   *  (interactive widgets). Returns null when the widget carries neither. */
  private chartFromRenderedWidget(widget: any, index: number): SavedSelfServiceChart | null {
    const now = new Date().toISOString();
    const id = String(widget?.widgetId ?? `rebuilt-${index}`);
    const chartType = String(widget?.type ?? widget?.selfService?.chartType ?? '').trim();
    if (!chartType) return null;

    const style = this.normalizeStyle(widget?.style);
    const title = String(widget?.title ?? style.titleOverride ?? chartType).trim() || chartType;
    const ss = widget?.selfService;
    const widgetConfig = widget?.widgetConfig;

    // Interactive widget (input-box, slider, …): no dataset; settings on widgetConfig.
    if (!ss && widgetConfig && typeof widgetConfig === 'object') {
      return {
        id,
        name: title,
        createdAt: now,
        updatedAt: now,
        chartType,
        aggregation: 'sum',
        mapping: {},
        filterKey: String(widget?.filterKey ?? ''),
        filterListener: [],
        dataset: {
          fileName: String(widgetConfig?.label ?? title),
          sourceType: 'csv',
          columns: Array.isArray(widget?.computationColumns) ? widget.computationColumns : [],
          rows: Array.isArray(widget?.computationRows) ? widget.computationRows : [],
        },
        config: {},
        style,
        widgetConfig: { ...widgetConfig },
      };
    }

    if (!ss || typeof ss !== 'object') return null;

    // Recover the mapping — prefer ssChartMeta.mapping (verbatim), else derive
    // from the flattened selfService keys buildWidgetEntry spread out.
    const metaMapping = widget?.ssChartMeta?.mapping;
    const mapping: Record<string, string> =
      metaMapping && typeof metaMapping === 'object' ? { ...metaMapping } : {};
    if (!Object.keys(mapping).length) {
      if (ss.xAxis) mapping['xAxis'] = String(ss.xAxis);
      if (ss.yAxis) mapping['yAxis'] = String(ss.yAxis);
      if (ss.categoryKey && !mapping['category'] && !mapping['xAxis']) mapping['category'] = String(ss.categoryKey);
      if (ss.valueKey && !mapping['value'] && !mapping['yAxis']) mapping['value'] = String(ss.valueKey);
      if (ss.sizeKey) mapping['size'] = String(ss.sizeKey);
      if (ss.colorByKey) mapping['colorBy'] = String(ss.colorByKey);
      if (ss.kpiKey && !mapping['value']) mapping['value'] = String(ss.kpiKey);
    }

    const rows = Array.isArray(ss.rawRows)
      ? ss.rawRows
      : Array.isArray(widget?.data) ? widget.data : [];
    const cols = Array.isArray(ss.columns)
      ? ss.columns.map((c: any) => ({ name: String(c?.name ?? ''), type: c?.type ?? 'string' }))
      : this.inferColumnsFromRows(rows);

    return {
      id,
      name: title,
      createdAt: now,
      updatedAt: now,
      chartType,
      aggregation: ss.aggregation ?? 'sum',
      mapping: mapping as SavedSelfServiceChart['mapping'],
      seriesField: ss.seriesField || undefined,
      sortBy: ss.sortBy || undefined,
      sortDir: ss.sortDir || undefined,
      topN: ss.topN ?? null,
      topNDirection: ss.topNDirection || undefined,
      dateGranularity: ss.dateGranularity || undefined,
      tooltipColumns: Array.isArray(widget?.tooltipColumns) ? [...widget.tooltipColumns] : undefined,
      comparisonColumn: widget?.comparisonColumn || undefined,
      referenceValue: widget?.referenceValue ?? undefined,
      style,
      filterKey: String(widget?.filterKey ?? ''),
      filterListener: [],
      dataset: {
        fileName: title,
        sourceType: 'csv',
        columns: cols,
        rows,
      },
      config: {},
    };
  }

  /** Infer dataset columns from the first row when a widget didn't carry an
   *  explicit column list. Types default to string. */
  private inferColumnsFromRows(rows: any[]): Array<{ name: string; type: any }> {
    const first = Array.isArray(rows) && rows.length ? rows[0] : null;
    if (!first || typeof first !== 'object') return [];
    return Object.keys(first).map((name) => ({ name, type: 'string' }));
  }

  /** Recover builder-level GlobalFilter[] from a rendered `filters` block. */
  private filtersFromRendered(filters: any): GlobalFilter[] {
    const list = Array.isArray(filters?.filters)
      ? filters.filters
      : Array.isArray(filters) ? filters : [];
    const out: GlobalFilter[] = [];
    for (const f of list) {
      const key = String(f?.key ?? f?.filterKey ?? f?.attributeName ?? '').trim();
      if (!key) continue;
      out.push({
        key,
        label: String(f?.label ?? f?.title ?? key),
        isMultiSelect: f?.isMultiSelect !== false,
        ...(f?.parentKey ? { parentKey: String(f.parentKey) } : {}),
      });
    }
    return out;
  }

  // Walks every chart dataset in `items` that exposes `filter.key` and
  // returns the set of unique non-empty values to use as dropdown options
  // in the published dashboard view (where there is no backend API).
  private computePublishedFilterOptions(
    filter: GlobalFilter,
    items: DashboardDraftItem[],
  ): string[] {
    const values = new Set<string>();
    items.forEach((item) => {
      const chart = this.resolveDashboardChart(item);
      if (!chart) return;
      if (!chart.dataset.columns.some((column) => column.name === filter.key)) return;
      chart.dataset.rows.forEach((row) => {
        const raw = row[filter.key];
        if (raw === undefined || raw === null) return;
        const value = String(raw).trim();
        if (value) values.add(value);
      });
    });
    return Array.from(values).sort();
  }

  buildWidgetId(chartId: string): string {
    return chartId.startsWith('widget-') ? chartId : `widget-${chartId}`;
  }

  /**
   * Published-card height per interactive widget kind. Compact controls get a
   * tight height so they don't float in a chart-sized card; upload/form/table
   * widgets keep more room. Kept here (not in CSS) because the published layout
   * is config-driven and reads `height` off each widget entry.
   */
  private widgetCardHeight(previewKind: ChartPreviewKind | undefined, cfg: WidgetConfig = {}): string {
    switch (previewKind) {
      case 'input-box':
      case 'button':
      case 'notes':
        return '120px';
      case 'slider':
      case 'slider-range':
      case 'range-bidirectional-slider':
        return '140px';
      case 'upload-file':
        return '160px';
      case 'configurable-form': {
        // Grow with the field count so a 1-field form isn't stranded in a tall
        // card and a 6-field form isn't cramped: label + Submit ≈ 96px, each
        // field ≈ 64px, clamped to a sane range.
        const fieldCount = Math.max(1, cfg.fields?.length ?? 1);
        const px = Math.min(420, 96 + fieldCount * 64);
        return `${px}px`;
      }
      case 'editable-table': {
        // header + visible rows (each ≈ 34px), clamped so it neither floats
        // nor runs away; extra rows scroll inside the card.
        const rowCount = Math.max(1, cfg.rows?.length ?? 1);
        const px = Math.min(360, 80 + rowCount * 34);
        return `${px}px`;
      }
      default:
        return '160px';
    }
  }
  private resolveTableColumns(chart: SavedSelfServiceChart, item?: DashboardDraftItem): string[] {
    const allColumns = chart.dataset.columns.map((column) => column.name);
    const configured = item?.tableColumns?.length ? item.tableColumns : undefined;
    if (!configured?.length) {
      return allColumns;
    }
    const selected = configured.filter((name) => allColumns.includes(name));
    return selected.length ? selected : allColumns;
  }

  private buildWidgetEntry(
    chart: SavedSelfServiceChart,
    item: DashboardDraftItem,
    items: DashboardDraftItem[],
  ): Record<string, unknown> {
    // WidgetId is keyed off item.id (not chart.id) so the same saved chart
    // dropped twice into a dashboard renders into two distinct DOM elements.
    // The shared chart components do document.getElementById(widgetId); reused
    // ids would point both widgets at the same div, leaving one blank.
    // Fall back to chart.id + index when item.id is missing (older saved
    // items from before the per-item-id era) so we still get unique ids.
    const itemKey = item.id || `${chart.id}-${items.indexOf(item)}`;
    const widgetId = this.buildWidgetId(itemKey);
    const filterListener = this.buildListenerWidgetIds(chart, items, item, widgetId);
    // The listener editor's "Publishes filter key" writes an item-level override
    // (item.filterKeyOverride), so the published widget must honour it — mirroring
    // the builder's getEffectiveFilterKey(). Using chart.filterKey alone drops the
    // override, leaving the published widget with an empty filterKey; its cross-filter
    // then no-ops (ChartFilterHandlerService.updateFilterSelection early-returns when
    // filterKey is empty) and clicks never publish to the listening charts.
    const effectiveFilterKey =
      (item.filterKeyOverride && item.filterKeyOverride.trim()) || chart.filterKey || '';
    const tableColumns = this.resolveTableColumns(chart, item);

    const style = this.normalizeStyle(chart.style);
    const requiresCategoryMapping = this.state.chartTypes()
      .find((item) => item.value === chart.chartType)?.requiredMappings.includes('category');
    const aggCategoryKey = requiresCategoryMapping
      ? (chart.mapping.category || '')
      : (chart.mapping.xAxis || '');
    const aggValueKey = requiresCategoryMapping
      ? (chart.mapping.value || '')
      : (chart.mapping.yAxis || '');
    const base: Record<string, unknown> = {
      widgetId,
      type: chart.chartType,
      title: style.titleOverride || chart.name,
      class: 'col-12 col-md-6 col-sm-12 col-lg-4',
      filterKey: effectiveFilterKey,
      filterListener,
      isMultiSelect: true,
      // Sentinel apiUrl: several components (clustered-bar, etc.) early-return
      // when no apiUrl is set and never call ConfigService.getConfig — which is
      // where the `selfService` short-circuit lives. getConfig returns the
      // reshaped self-service data BEFORE any HTTP, so this URL is never fetched;
      // it only routes those components into the self-service pipeline.
      apiUrl: 'self-service://local',
      // NOTE on showLabels: the shared library uses `chartConfig.showLabels` to
      // gate AXIS labels (X categories + Y values) and a separate
      // `chartConfig.showDataLabels` for on-bar data labels. The builder's
      // `style.showLabels` only means "data labels on bars" — never axis labels.
      chartConfig: { colorSyncEnabled: true, palette: style.palette, showLegend: style.showLegend, legendPosition: style.legendPosition, showLabels: true, showDataLabels: style.showLabels, labelMode: style.labelMode, numberFormat: style.numberFormat, decimals: style.decimals, xAxisLabel: style.xAxisLabel, yAxisLabel: style.yAxisLabel },
      // `selfService` carries the raw dataset and aggregation parameters.
      // ConfigService.getConfig() detects this payload and calls
      // computeSelfServiceData(meta, filters) every time global filters or
      // cross-filter publishes change — that's what makes filter dropdowns
      // and chart-click cross-filtering actually re-narrow the charts in
      // the published view.
      selfService: {
        rawRows: chart.dataset.rows,
        columns: chart.dataset.columns.map((c) => ({ name: c.name, type: c.type })),
        chartType: chart.chartType,
        categoryKey: aggCategoryKey,
        valueKey: aggValueKey,
        aggregation: chart.aggregation || 'sum',
        sortBy: chart.sortBy || undefined,
        sortDir: chart.sortBy ? (chart.sortDir || 'asc') : undefined,
        topN: chart.topN ?? undefined,
        topNDirection: chart.topN ? (chart.topNDirection || 'top') : undefined,
        dateGranularity: chart.dateGranularity && chart.dateGranularity !== 'none' ? chart.dateGranularity : undefined,
        selfWidgetId: widgetId,
        // Extra mapping carried so computeSelfServiceData can reshape multi-series,
        // scatter/bubble, KPI, treemap, gantt and table charts to each
        // component's expected input shape (not just keyed rows).
        seriesField: chart.seriesField || undefined,
        xAxis: chart.mapping.xAxis || undefined,
        yAxis: chart.mapping.yAxis || undefined,
        sizeKey: chart.mapping.size || undefined,
        colorByKey: chart.mapping.colorBy || undefined,
        kpiKey: chart.mapping.value || undefined,
        tableColumns,
      },
      // `data` kept as a static fallback for tooling that doesn't run the
      // selfService pipeline (e.g. preview without wrapper). Live published
      // rendering goes through ConfigService.getConfig ? computeSelfServiceData.
      data: this.buildPublishedDataset(chart, item),
      seriesField: chart.seriesField || undefined,
      sortBy: chart.sortBy || undefined,
      sortDir: chart.sortBy ? (chart.sortDir || 'asc') : undefined,
      topN: chart.topN ?? undefined,
      topNDirection: chart.topN ? (chart.topNDirection || 'top') : undefined,
      dateGranularity: chart.dateGranularity && chart.dateGranularity !== 'none' ? chart.dateGranularity : undefined,
      tooltipColumns: chart.tooltipColumns?.length ? [...chart.tooltipColumns] : undefined,
      comparisonColumn: chart.comparisonColumn || undefined,
      referenceValue: chart.referenceValue ?? undefined,
      style,
    };

    const chartTypeDef = this.state.chartTypes().find((item) => item.value === chart.chartType);
    const previewKind = chartTypeDef?.previewKind;
    const requiresCategory = chartTypeDef?.requiredMappings.includes('category');
    const supportsAggregation = chartTypeDef?.supportsAggregation !== false;

    // For every ECharts chart type (everything except KPI/table, which render
    // via dedicated components), carry the chart "meta" so the published view
    // can rebuild the EXACT option the builder preview draws, using the shared
    // SelfServiceChartOptionBuilder. The generic SelfServiceEChartsComponent
    // re-runs that builder against freshly filtered rows on every filter change,
    // so the published chart is identical to the preview AND stays reactive.
    // This is lightweight (no baked data arrays) so it never blows the
    // localStorage quota. KPI/table are handled by NumberStats/Table below.
    const isWidget = isWidgetPreviewKind(previewKind);

    if (previewKind !== 'kpi' && previewKind !== 'table' && previewKind !== 'generic-drill-down' && !isWidget) {
      // The meta is fully self-describing so the published component needs NO
      // hardcoded lookup tables: it carries the resolved palette colours, the
      // chart's previewKind, and the builtin-aggregation values. Add a new chart
      // type or palette in this component only — the published view follows
      // automatically from this meta.
      base['ssChartMeta'] = {
        chartType: chart.chartType,
        previewKind,
        mapping: { ...chart.mapping },
        aggregation: chart.aggregation || 'sum',
        seriesField: chart.seriesField || '',
        titleText: style.titleOverride || chart.name || '',
        style,
        shapeOptions: this.getShapeOptionsFromChart(chart),
        customAggregations: this.customAggregations.map((a) => ({ ...a })),
        resolvedPalette: this.dataUtils.getPaletteColors(style.palette),
        builtinAggValues: this.builtinAggregations.map((a) => a.value),
      };
      base['ssEChartsType'] = previewKind;
      // Match the builder's arrange-view card chart height (.dashboard-chart
      // is 240px) so the published chart is the same size as in the builder —
      // not the grid's 20vh default (too short) nor an oversized fixed box.
      base['height'] = `${STANDARD_CARD_HEIGHT_PX}px`;
    }

    if (isWidget) {
      // Interactive widgets (input-box, slider, button, notes, …) render via
      // their dedicated published components, routed by `type` in
      // ChartRendererService — exactly like the static dashboard widgets. They
      // carry no dataset/meta; their settings come from `widgetConfig`, spread
      // onto the entry so each component reads them off `this.data`.
      const widgetCfg = chart.widgetConfig || {};
      return {
        ...base,
        type: chart.chartType,
        // ECharts/selfService machinery is irrelevant for widgets — drop it so
        // the renderer routes purely by `type` to the widget component.
        ssChartMeta: undefined,
        selfService: undefined,
        // Marker the published ChartRendererService keys off: render this entry
        // with the SAME SelfServiceWidgetComponent the builder preview uses, so
        // the published widget is identical to the Arrange-view preview instead
        // of routing to a hardcoded production widget component (which ignores
        // widgetConfig and shows its own defaults). Carries the preview kind so
        // the renderer needs no chart-type→kind lookup table.
        ssWidgetKind: previewKind,
        // Height tuned per widget so compact controls (input box, slider,
        // notes, button) don't float inside an oversized chart-sized card.
        // Data-ish widgets (upload, form, editable table) keep more room.
        height: this.widgetCardHeight(previewKind, widgetCfg),
        // Common widget settings the published components read directly.
        label: widgetCfg.label || chart.name,
        title: style.titleOverride || widgetCfg.label || chart.name,
        placeholder: widgetCfg.placeholder,
        min: widgetCfg.min,
        max: widgetCfg.max,
        step: widgetCfg.step,
        defaultValue: widgetCfg.defaultValue,
        rangeStart: widgetCfg.rangeStart,
        rangeEnd: widgetCfg.rangeEnd,
        buttonLabel: widgetCfg.buttonLabel,
        notesText: widgetCfg.notesText,
        notes: widgetCfg.notesText ? [{ id: 1, label: widgetCfg.label || '', text: widgetCfg.notesText }] : undefined,
        submitLabel: widgetCfg.submitLabel,
        fields: widgetCfg.fields,
        columns: widgetCfg.columns,
        rows: widgetCfg.rows,
        accept: widgetCfg.accept,
        multiple: widgetCfg.multiple,
        computationEntityName: chart.dataset?.fileName,
        computationColumns: chart.dataset?.columns ?? [],
        computationRows: chart.dataset?.rows ?? [],
        // Keep the full widget config under a stable key too, for any component
        // that prefers to read a single object.
        widgetConfig: { ...widgetCfg },
      };
    }

    if (previewKind === 'kpi') {
      return {
        ...base,
        // KPI cards are a single stat — keep them compact so the number isn't
        // stranded in a tall card (matches the static-dashboard KPI height).
        height: '120px',
        valueKey: chart.mapping.value,
        // NumberStatsComponent reads the scalar from `this.data.key`; the
        // selfService pipeline returns { [valueKey]: aggregated }.
        key: chart.mapping.value,
        aggregation: chart.aggregation,
        comparisonValue: style.comparisonValue,
        // Number format/scale/decimals chosen in Chart Builder — without these,
        // NumberStatsComponent (the real published KPI renderer) has no format
        // info and falls back to a bare String(value), silently dropping the
        // chosen formatting once the dashboard is published.
        numberFormat: style.numberFormat,
        numberScale: style.numberScale,
        decimals: style.decimals,
      };
    }

    if (previewKind === 'table') {
      const columnNames = tableColumns;
      // Show ~7 rows then scroll — matching the self-service dashboard table.
      // Published rows are ~29px (padding 6+6 + ~16 text + 1 border) incl. header,
      // so header + 7 rows ≈ 8×29 ≈ 232px. The table fills the card
      // (.ss-published CSS) so there is no empty gap below; extra rows scroll.
      const VISIBLE_ROWS = 7;
      const ROW_PX = 29;
      const tableHeight = (VISIBLE_ROWS + 1) * ROW_PX; // header + 7 rows ≈ 232px
      return {
        ...base,
        height: `${tableHeight}px`,
        columns: columnNames,
        // TableComponent reads rows from `res.options` and column wiring from
        // `this.data.headerColumns` ([{ key, label }]).
        headerColumns: columnNames.map((name) => ({ key: name, label: name })),
        rowLimit: style.tableRowLimit,
        // Tag so TableComponent lets columns keep their natural width and the
        // card scrolls horizontally instead of squeezing/clipping wide tables.
        selfServiceTable: true,
        enableColumnManagement: false,
        hideColumnManagement: true,
      };
    }

    if (previewKind === 'generic-drill-down') {
      // The drill-down renders via GenericDrillDownComponent, which reads flat
      // rows + level/measure columns off the reshaped selfService payload at
      // runtime (computeSelfServiceData → reshapeDrillDown).
      const columnNames = chart.dataset.columns.map((c) => c.name);
      // Level columns = non-numeric columns (same classification as
      // reshapeDrillDown), in column order; one drill level each.
      const levelColumns = chart.dataset.columns
        .filter((c) => {
          const t = (c.type || '').toLowerCase();
          return !(t === 'number' || t === 'numeric' || t === 'integer' || t === 'float');
        })
        .map((c) => c.name);
      // Cross-filter needs a real column as the publish key. The drill-down has
      // no category/xAxis mapping, so the builder's autoDefaultFilterKey() never
      // seeds one — default it to the FIRST level column here so row clicks
      // publish a matching filter out of the box (the component still publishes
      // the CLICKED level's column too). An explicit builder choice wins.
      const drillDownFilterKey = effectiveFilterKey || levelColumns[0] || '';
      return {
        ...base,
        filterKey: drillDownFilterKey,
        // Use the SAME 240px card height as ECharts charts (base['height'] above)
        // so the drill-down card lines up with the chart/table cards in the
        // published grid instead of shrinking to its collapsed content. The tree
        // fills the card and scrolls internally when it has more rows than fit.
        height: DRILL_DOWN_CARD_HEIGHT_PX + 'px',
        columns: columnNames,
        columnLabels: Object.fromEntries(columnNames.map((name) => [name, name])),
        // Carry the level columns so the component resolves the clicked level's
        // publish key deterministically instead of re-inferring from row types.
        levelColumns,
        // Any explicit measure choices (none from the current builder UI) would
        // go here; absent them the component infers measures from column types.
      };
    }

    if (requiresCategory) {
      return {
        ...base,
        categoryKey: chart.mapping.category,
        valueKey: chart.mapping.value,
        ...(supportsAggregation ? { aggregation: chart.aggregation } : {}),
      };
    }

    // Scatter / bubble family consume { points: [[x, y, size, label]] }.
    if (previewKind === 'scatter' || previewKind === 'bubble' || previewKind === 'quadrant-bubble') {
      return {
        ...base,
        xAxis: chart.mapping.xAxis,
        yAxis: chart.mapping.yAxis,
        ...(chart.mapping.size ? { sizeKey: chart.mapping.size } : {}),
        ...(chart.mapping.colorBy ? { colorByKey: chart.mapping.colorBy } : {}),
      };
    }

    // Gantt is now rendered by the generic SelfServiceEChartsComponent like
    // every other ECharts type — it rebuilds the option from the shared builder
    // using the filtered raw rows in `selfService`. So we must KEEP selfService
    // here (an earlier version stripped it, which left the published gantt with
    // no rows to build from and rendered blank).
    if (previewKind === 'gantt') {
      return { ...base };
    }

    const xKey = chart.mapping.xAxis;
    const yKey = chart.mapping.yAxis;
    const seriesCol = chart.seriesField && chart.seriesField !== xKey ? chart.seriesField : '';

    // Line-column reads number arrays at config-named paths; computeSelfServiceData
    // and buildPublishedDataset both emit { category, barValues, lineValues }.
    if (chart.chartType === 'line-column-chart') {
      return {
        ...base,
        categoryKey: xKey, valueKey: yKey, xAxis: 'category',
        barSeries: 'barValues', lineSeries: 'lineValues',
        seriesNames: [yKey, yKey], yAxisNames: [yKey, yKey],
        ...(supportsAggregation ? { aggregation: chart.aggregation } : {}),
      };
    }

    // Multi-series bar-line combo reads a flat row array (payload.data) plus
    // barSeries/lineSeries field config.
    if (chart.chartType === 'multi-series-bar-line-combo-chart') {
      return {
        ...base,
        categoryKey: xKey, valueKey: yKey,
        chartConfig: { ...(base['chartConfig'] as Record<string, unknown>), categoryField: 'category' },
        barSeries: [{ name: yKey, valueField: 'value' }],
        lineSeries: [{ name: yKey, valueField: 'value' }],
        ...(supportsAggregation ? { aggregation: chart.aggregation } : {}),
      };
    }

    // Multi-level comparison reads a `bar` array keyed by engine/year/value.
    // reshapeCategorySeries emits bar rows of { [xKey]: cat, [seriesCol]: name,
    // [yKey]: value }, so engineKey=series (the grouping), yearKey=x, valueKey=y.
    if (chart.chartType === 'multi-level-comparison-chart') {
      return {
        ...base,
        categoryKey: xKey, valueKey: yKey, xAxis: xKey,
        engineKey: seriesCol || xKey, yearKey: xKey,
        ...(supportsAggregation ? { aggregation: chart.aggregation } : {}),
      };
    }

    // Stacked-horizontal renders via its pre-formatted { categories, series }
    // branch. We deliberately omit displacement/kind/quantity keys so it does
    // NOT take the domain-specific cummins/non-cummins path (which would bail).
    if (SelfServiceChartPersistenceService.HORIZONTAL_STACK_CHART_TYPES.has(chart.chartType)) {
      return {
        ...base,
        categoryKey: xKey, valueKey: yKey, xAxis: xKey, seriesKey: seriesCol || 'series',
        ...(supportsAggregation ? { aggregation: chart.aggregation } : {}),
      };
    }

    // Bar-Line / Multi-Bar-Multi-Line: the component reads each category's value
    // from row[barValueField] / row[lineValueField] (defaulting to 'gross_value'
    // / 'percentage'). The union's { category, series } branch stores per-category
    // records keyed by `value`, so point both metric fields at 'value' and the
    // dimension at 'year' (the series name field) so bars AND the line draw from
    // the single aggregated metric.
    if (chart.chartType === 'bar-line' || chart.chartType === 'mbar-mline') {
      return {
        ...base,
        categoryKey: xKey, valueKey: yKey, xAxis: xKey, seriesKey: seriesCol || 'series',
        barValueField: 'value', lineValueField: 'value', dimensionField: 'year',
        ...(supportsAggregation ? { aggregation: chart.aggregation } : {}),
      };
    }

    // Stacked / clustered / area / line / mixed family: pivot via categoryKey /
    // seriesKey / valueKey. The reshape emits a union { category, series, bar }.
    if (SelfServiceChartPersistenceService.CATEGORY_SERIES_CHART_TYPES.has(chart.chartType)) {
      return {
        ...base,
        categoryKey: xKey, valueKey: yKey, xAxis: xKey,
        seriesKey: seriesCol || 'series',
        ...(supportsAggregation ? { aggregation: chart.aggregation } : {}),
      };
    }

    // Plain bar / line / horizontal-bar and waterfall: keyed rows / parallel arrays.
    return {
      ...base,
      categoryKey: xKey,
      valueKey: yKey,
      xAxis: xKey,
      ...(supportsAggregation ? { aggregation: chart.aggregation } : {}),
    };
  }

  // For cross-filter wiring in the published view: a chart's filterListener
  // stores chart IDs (saved-chart-level), but the published widgets are keyed
  // by item.id. Fan out each listener chart-id to all dashboard items using
  // that chart, then map to widget IDs. Item-level overrides on the source
  // (listenerItemIds) take precedence — they already store item ids directly.
  private buildListenerWidgetIds(
    chart: SavedSelfServiceChart,
    items: DashboardDraftItem[],
    sourceItem: DashboardDraftItem,
    selfWidgetId: string,
  ): string[] {
    const customWidgetIds = chart.filterListenerCustom || [];
    let listenerWidgetIds: string[];
    if (Array.isArray(sourceItem.listenerItemIds)) {
      listenerWidgetIds = sourceItem.listenerItemIds.map((itemId) => this.buildWidgetId(itemId));
    } else {
      const listenerChartIds = chart.filterListener || [];
      listenerWidgetIds = listenerChartIds.flatMap((listenerChartId) =>
        items
          .filter((it) => it.chartId === listenerChartId && it.id !== sourceItem.id)
          .map((it) => this.buildWidgetId(it.id)),
      );
    }
    const merged: string[] = [];
    for (const id of [...listenerWidgetIds, ...customWidgetIds]) {
      if (id === selfWidgetId) continue;
      if (!merged.includes(id)) merged.push(id);
    }
    return merged;
  }

  mergeListenerWidgetIds(
    dropdownChartIds: string[],
    customWidgetIds: string[] | undefined,
    selfWidgetId: string,
  ): string[] {
    const dropdownIds = dropdownChartIds.map((id) => this.buildWidgetId(id));
    const customIds = customWidgetIds || [];
    const merged: string[] = [];
    for (const id of [...dropdownIds, ...customIds]) {
      if (id === selfWidgetId) {
        continue;
      }
      if (!merged.includes(id)) {
        merged.push(id);
      }
    }
    return merged;
  }

  // Produces data in the shape each shared chart component expects when it
  // reads the static `data` field directly (some components check
  // `this.data?.data` before the apiUrl/selfService path). This MUST mirror
  // the shapes that computeSelfServiceData (the live filtered path) produces,
  // so a chart renders identically whichever path its component takes:
  //   - bar / line / horizontal-bar: keyed rows [{[catKey]: name, [valKey]: value}]
  //   - pie-chart:   literal [{name, value}, ...]
  //   - donut-chart: { bar: keyedRows } (DonutChartComponent reads res?.bar)
  //   - stacked/clustered/line-area/combo: { category: [...], series: [{name, data}] }
  //   - waterfall:   { category: [...], series: number[] }
  //   - scatter/bubble: { points: [[x, y, size, label], ...] }
  //   - treemap:     [{ name, value, children:[{name,value}] }]
  //   - kpi:         { [valueKey]: aggregatedScalar }
  //   - table:       { options: rows[] }
  //   - gantt:       [{ name, start, end, group }]
  private buildPublishedDataset(chart: SavedSelfServiceChart, item?: DashboardDraftItem): unknown {
    const chartTypeDef = this.state.chartTypes().find((item) => item.value === chart.chartType);
    const previewKind = chartTypeDef?.previewKind;
    const supportsAggregation = chartTypeDef?.supportsAggregation !== false;

    // Data table: { options: rows } so TableComponent (reads res.options) renders.
    if (previewKind === 'table') {
      const columnNames = this.resolveTableColumns(chart, item);
      const rows = chart.dataset.rows.map((row) => {
        const projected: Record<string, string> = {};
        for (const column of columnNames) {
          projected[column] = row[column] ?? '';
        }
        return projected;
      });
      const limited = chart.topN && chart.topN > 0 ? rows.slice(0, chart.topN) : rows;
      return { options: limited };
    }

    // Drill-down: flat rows + inferred level/measure columns. Mirrors the
    // runtime reshapeDrillDown so this static fallback renders identically.
    if (previewKind === 'generic-drill-down') {
      const rows = chart.dataset.rows.map((row) => ({ ...row }));
      const limited = chart.topN && chart.topN > 0 ? rows.slice(0, chart.topN) : rows;
      const levelColumns: string[] = [];
      const measureColumns: string[] = [];
      const sample = limited.find((r) => r && typeof r === 'object') || {};
      for (const col of chart.dataset.columns) {
        const declared = (col.type || '').toLowerCase();
        const isNumeric = declared
          ? ['number', 'numeric', 'integer', 'float'].includes(declared)
          : Number.isFinite(this.dataUtils.toNumber(sample[col.name]));
        (isNumeric ? measureColumns : levelColumns).push(col.name);
      }
      return { options: limited, levelColumns, measureColumns };
    }

    // KPI: single aggregated scalar keyed by the value column.
    if (previewKind === 'kpi') {
      const valueKey = chart.mapping.value || '';
      if (!valueKey) return { value: 0 };

      const aggregation = chart.aggregation || 'sum';
      const columnType = chart.dataset.columns.find((column) => column.name === valueKey)?.type;
      const rawValues = chart.dataset.rows.map((row) => String(row[valueKey] ?? '').trim());
      const nonEmptyValues = rawValues.filter((value) => value !== '');
      const numericResult = columnType === 'number' || aggregation === 'count' || aggregation === 'distinctCount';

      if (!numericResult) {
        if (aggregation === 'distinct') {
          return { [valueKey]: Array.from(new Set(nonEmptyValues)).join(', ') };
        }
        if (aggregation === 'last') {
          return { [valueKey]: nonEmptyValues[nonEmptyValues.length - 1] || '' };
        }
        return { [valueKey]: nonEmptyValues[0] || '' };
      }

      const values = rawValues.map((value) => this.dataUtils.toNumber(value));
      return { [valueKey]: this.dataUtils.aggregateValues(values, aggregation) };
    }
    // Treemap: one-level hierarchy [{ name, value, children:[{name,value}] }].
    if (previewKind === 'treemap') {
      const catKey = chart.mapping.category || '';
      const valKey = chart.mapping.value || '';
      if (!catKey || !valKey) return chart.dataset.rows.map((row) => ({ ...row }));
      const previousShapeOptions = this.currentShapeOptions;
      this.currentShapeOptions = this.getShapeOptionsFromChart(chart);
      let aggregated: Array<{ name: string; value: number }>;
      try {
        aggregated = this.dataUtils.aggregateByCategory(chart.dataset, catKey, valKey, chart.aggregation || 'sum', this.currentShapeOptions, this.customAggregations, this.builtinAggregations);
      } finally {
        this.currentShapeOptions = previousShapeOptions;
      }
      return aggregated
        .filter((a) => a.value > 0)
        .map((a) => ({ name: a.name, value: a.value, children: [{ name: a.name, value: a.value }] }));
    }

    // Quadrant bubble: { q1, q2, q3, q4 } split by median(x), median(y).
    if (previewKind === 'quadrant-bubble') {
      const xKey = chart.mapping.xAxis || '';
      const yKey = chart.mapping.yAxis || '';
      const sizeKey = chart.mapping.size || '';
      const labelKey = chart.mapping.colorBy || chart.seriesField || '';
      const pts = chart.dataset.rows.map((row) => ({
        x: this.dataUtils.toNumber(row[xKey]),
        y: this.dataUtils.toNumber(row[yKey]),
        size: sizeKey ? this.dataUtils.toNumber(row[sizeKey]) : 15,
        label: labelKey ? String(row[labelKey] ?? '') : '',
      }));
      const median = (values: number[]): number => {
        if (!values.length) return 0;
        const sorted = [...values].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
      };
      const mx = median(pts.map((p) => p.x));
      const my = median(pts.map((p) => p.y));
      const q1: unknown[] = [], q2: unknown[] = [], q3: unknown[] = [], q4: unknown[] = [];
      pts.forEach((p) => {
        const point = { x: p.x, y: p.y, size: p.size, label: p.label, name: p.label };
        if (p.x >= mx && p.y >= my) q1.push(point);
        else if (p.x < mx && p.y >= my) q2.push(point);
        else if (p.x < mx && p.y < my) q3.push(point);
        else q4.push(point);
      });
      return { q1, q2, q3, q4 };
    }

    // Scatter / bubble: { points: [[x, y, size, label], ...] } from raw rows.
    if (previewKind === 'scatter' || previewKind === 'bubble') {
      const xKey = chart.mapping.xAxis || '';
      const yKey = chart.mapping.yAxis || '';
      const sizeKey = chart.mapping.size || '';
      const labelKey = chart.mapping.colorBy || chart.seriesField || '';
      const points = chart.dataset.rows.map((row) => {
        const x = this.dataUtils.toNumber(row[xKey]);
        const y = this.dataUtils.toNumber(row[yKey]);
        const size = sizeKey ? this.dataUtils.toNumber(row[sizeKey]) : Math.max(Math.abs(x), Math.abs(y), 1);
        const label = labelKey ? String(row[labelKey] ?? '') : '';
        return [x, y, size, label];
      });
      return { points };
    }

    // Gantt: aggregate per-category into task records with name/start/end fields
    // that the DVM gantt-chart component can directly consume (it searches for
    // 'name' as the task label and 'start'/'end' as ISO-8601 date strings).
    if (previewKind === 'gantt') {
      const startKey = chart.mapping.xAxis || '';
      const endKey = chart.mapping.yAxis || '';
      const catKey = chart.seriesField || startKey;
      const sameCol = startKey === endKey;
      const groups = new Map<string, { start: number; end: number }>();
      chart.dataset.rows.forEach(row => {
        const cat = String(row[catKey] || '(blank)');
        const rawA = this.dataUtils.toNumber(row[startKey]);
        const rawB = this.dataUtils.toNumber(row[endKey]);
        const s = sameCol ? rawA : Math.min(rawA, rawB);
        const e = sameCol ? rawA + 1 : Math.max(rawA, rawB);
        if (e <= s) return;
        const existing = groups.get(cat);
        if (!existing) { groups.set(cat, { start: s, end: e }); }
        else { existing.start = Math.min(existing.start, s); existing.end = Math.max(existing.end, e); }
      });

      // Determine whether the start values represent real dates.
      // Year-like (1900-2100) and Unix timestamps (>1e8 seconds) are valid.
      // Financial/ordinal values (e.g. Revenue=50000) are NOT valid dates and
      // would map to 1970-01-01, so fall back to a synthetic monthly timeline.
      const startVals = Array.from(groups.values()).map(r => r.start);
      const haveMeaningfulDates = startVals.length > 0 && startVals.every(
        v => (v >= 1900 && v <= 2100) || v > 1e8,
      );

      if (haveMeaningfulDates) {
        return Array.from(groups.entries())
          .filter(([, r]) => r.end > r.start)
          .map(([name, r]) => ({ name, start: this.toGanttDateString(r.start), end: this.toGanttDateString(r.end), group: '' }));
      }

      // Fallback: values are not dates (e.g. financial figures).
      // Assign tasks to consecutive calendar months, detecting month-name order.
      const MONTH_ABBR = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
      const monthIdx = (n: string): number => MONTH_ABBR.findIndex(m => n.toLowerCase().startsWith(m));
      const baseYear = new Date().getFullYear();
      const sorted = Array.from(groups.entries()).sort(([nA, a], [nB, b]) => {
        const mA = monthIdx(nA); const mB = monthIdx(nB);
        return (mA >= 0 && mB >= 0) ? mA - mB : a.start - b.start;
      });
      return sorted.map(([name], idx) => {
        const m = idx % 12;
        const yr = baseYear + Math.floor(idx / 12);
        const nm = (m + 1) % 12; const nyr = m === 11 ? yr + 1 : yr;
        // group is left empty: each category is its own row, so a group label
        // would just duplicate the task name (rowLabelMode 'group-task' renders
        // "Jan • Jan"). Empty group => the row shows just the month name once.
        return { name, start: `${yr}-${String(m + 1).padStart(2, '0')}-01`, end: `${nyr}-${String(nm + 1).padStart(2, '0')}-01`, group: '' };
      });
    }

    const requiresCategory = chartTypeDef?.requiredMappings.includes('category');
    const categoryKey = requiresCategory ? (chart.mapping.category || '') : (chart.mapping.xAxis || '');
    const valueKey = requiresCategory ? (chart.mapping.value || '') : (chart.mapping.yAxis || '');

    if (!categoryKey || !valueKey || !supportsAggregation) {
      return chart.dataset.rows.map((row) => ({ ...row }));
    }

    // Waterfall family: pre-built ECharts series (Placeholder + Increase +
    // Decrease) carrying the running-balance math — the component spreads each
    // series entry straight into ECharts. Mirrors computeSelfServiceData.
    if (SelfServiceChartPersistenceService.WATERFALL_CHART_TYPES.has(chart.chartType)) {
      const previous = this.currentShapeOptions;
      this.currentShapeOptions = this.getShapeOptionsFromChart(chart);
      try {
        const agg = this.dataUtils.aggregateByCategory(chart.dataset, categoryKey, valueKey, chart.aggregation || 'sum', this.currentShapeOptions, this.customAggregations, this.builtinAggregations);
        const values = agg.map((a) => Number(a.value) || 0);
        const placeholder: Array<number | string> = [];
        const increase: Array<number | string> = [];
        const decrease: Array<number | string> = [];
        let running = 0;
        for (const value of values) {
          if (value >= 0) { placeholder.push(running); increase.push(value); decrease.push('-'); }
          else { placeholder.push(running + value); increase.push('-'); decrease.push(-value); }
          running += value;
        }
        return {
          category: agg.map((a) => a.name),
          series: [
            { name: 'Placeholder', type: 'bar', stack: 'total', silent: true, itemStyle: { color: 'transparent', borderColor: 'transparent' }, emphasis: { itemStyle: { color: 'transparent', borderColor: 'transparent' } }, data: placeholder },
            { name: 'Increase', type: 'bar', stack: 'total', itemStyle: { color: '#10b981' }, data: increase },
            { name: 'Decrease', type: 'bar', stack: 'total', itemStyle: { color: '#ef4444' }, data: decrease },
          ],
        };
      } finally {
        this.currentShapeOptions = previous;
      }
    }

    // Waterfall-stack (generic mode): { category, series:[{name,data}] }.
    if (SelfServiceChartPersistenceService.WATERFALL_STACK_CHART_TYPES.has(chart.chartType)) {
      const seriesField = chart.seriesField && chart.seriesField !== categoryKey ? chart.seriesField : '';
      const previous = this.currentShapeOptions;
      this.currentShapeOptions = this.getShapeOptionsFromChart(chart);
      try {
        if (seriesField) {
          const pivot = this.dataUtils.aggregateByCategoryAndSeries(
            chart.dataset, categoryKey, valueKey, seriesField, chart.aggregation || 'sum',
            this.currentShapeOptions, this.customAggregations, this.builtinAggregations,
          );
          return {
            category: pivot.categories,
            series: pivot.seriesNames.map((name, si) => ({
              name, data: pivot.categories.map((_, ci) => pivot.data[ci][si]),
            })),
          };
        }
        const agg = this.dataUtils.aggregateByCategory(chart.dataset, categoryKey, valueKey, chart.aggregation || 'sum', this.currentShapeOptions, this.customAggregations, this.builtinAggregations);
        return { category: agg.map((a) => a.name), series: [{ name: valueKey, data: agg.map((a) => a.value) }] };
      } finally {
        this.currentShapeOptions = previous;
      }
    }

    // Line-column: { category, barValues, lineValues } at fixed paths.
    if (SelfServiceChartPersistenceService.LINE_COLUMN_CHART_TYPES.has(chart.chartType)) {
      const previous = this.currentShapeOptions;
      this.currentShapeOptions = this.getShapeOptionsFromChart(chart);
      try {
        const agg = this.dataUtils.aggregateByCategory(chart.dataset, categoryKey, valueKey, chart.aggregation || 'sum', this.currentShapeOptions, this.customAggregations, this.builtinAggregations);
        const values = agg.map((a) => a.value);
        return { category: agg.map((a) => a.name), barValues: values, lineValues: values };
      } finally {
        this.currentShapeOptions = previous;
      }
    }

    // Multi-series bar-line combo: flat row array under `data`.
    if (SelfServiceChartPersistenceService.COMBO_CHART_TYPES.has(chart.chartType)) {
      const previous = this.currentShapeOptions;
      this.currentShapeOptions = this.getShapeOptionsFromChart(chart);
      try {
        const agg = this.dataUtils.aggregateByCategory(chart.dataset, categoryKey, valueKey, chart.aggregation || 'sum', this.currentShapeOptions, this.customAggregations, this.builtinAggregations);
        return { data: agg.map((a) => ({ category: a.name, value: a.value })) };
      } finally {
        this.currentShapeOptions = previous;
      }
    }

    // Multi-series category charts: UNION { category, categories, series, bar }
    // mirroring computeSelfServiceData so a component renders identically whether
    // it reads the static `data` or the live selfService path. seriesField (when
    // set) pivots into one series per distinct value; otherwise a single series
    // named after the value column. Stacked-horizontal is included but gets the
    // `bar`-less shape (its `bar` branch routes into a domain-specific path).
    const isHorizontalStack = SelfServiceChartPersistenceService.HORIZONTAL_STACK_CHART_TYPES.has(chart.chartType);
    if (SelfServiceChartPersistenceService.CATEGORY_SERIES_CHART_TYPES.has(chart.chartType) || isHorizontalStack) {
      const seriesField = chart.seriesField && chart.seriesField !== categoryKey ? chart.seriesField : '';
      const seriesColName = seriesField || 'series';
      const previous = this.currentShapeOptions;
      this.currentShapeOptions = this.getShapeOptionsFromChart(chart);
      try {
        let categories: string[];
        let series: Array<{ name: string; type: string; data: number[]; value: number[] }>;
        if (seriesField) {
          const pivot = this.dataUtils.aggregateByCategoryAndSeries(
            chart.dataset, categoryKey, valueKey, seriesField, chart.aggregation || 'sum',
            this.currentShapeOptions, this.customAggregations, this.builtinAggregations,
          );
          categories = pivot.categories;
          series = pivot.seriesNames.map((name, si) => {
            const dataArr = categories.map((_, ci) => pivot.data[ci][si]);
            return { name, type: 'bar', data: dataArr, value: dataArr };
          });
        } else {
          const agg = this.dataUtils.aggregateByCategory(chart.dataset, categoryKey, valueKey, chart.aggregation || 'sum', this.currentShapeOptions, this.customAggregations, this.builtinAggregations);
          categories = agg.map((a) => a.name);
          const dataArr = agg.map((a) => a.value);
          series = [{ name: valueKey, type: 'bar', data: dataArr, value: dataArr }];
        }
        if (isHorizontalStack) {
          return { categories, series };
        }
        const bar: Array<Record<string, unknown>> = [];
        categories.forEach((cat, ci) => {
          series.forEach((s) => bar.push({ [categoryKey]: cat, [seriesColName]: s.name, [valueKey]: s.data[ci] }));
        });
        return { category: categories, categories, series, bar };
      } finally {
        this.currentShapeOptions = previous;
      }
    }

    // aggregateByCategory consults this.currentShapeOptions for sort/topN/granularity.
    const previousShapeOptions = this.currentShapeOptions;
    this.currentShapeOptions = this.getShapeOptionsFromChart(chart);
    let aggregated: Array<{ name: string; value: number }>;
    try {
      aggregated = this.dataUtils.aggregateByCategory(
        chart.dataset,
        categoryKey,
        valueKey,
        chart.aggregation || 'sum',
        this.currentShapeOptions,
        this.customAggregations,
        this.builtinAggregations,
      );
    } finally {
      this.currentShapeOptions = previousShapeOptions;
    }

    // Pie passes the row array straight to an ECharts pie series — needs the
    // literal {name, value} field names.
    if (chart.chartType === 'pie-chart') {
      return aggregated.map((item) => ({ name: item.name, value: item.value }));
    }

    const keyedRows = aggregated.map(({ name, value }) => ({
      [categoryKey]: name,
      [valueKey]: value,
    }));

    // Donut reads res?.bar — wrap accordingly.
    if (chart.chartType === 'donut-chart') {
      return { bar: keyedRows };
    }

    return keyedRows;
  }

  /**
   * Rebuild a saved dashboard's `config` payload from the (possibly just-edited)
   * saved charts it references. buildDashboardConfigFromItems is parameterized
   * with the layout context, so to rebuild a DIFFERENT dashboard's config we
   * pass THAT dashboard's own columns/status/publishedAt/global filters (not the
   * active signal values) — so re-saving a background dashboard never clobbers
   * the one the user is editing, and each dashboard keeps its own layout/status.
   */
  private rebuildDashboardConfig(dashboard: SavedSelfServiceDashboard): Record<string, unknown> {
    return this.buildDashboardConfigFromItems(
      dashboard.name,
      dashboard.items,
      this.clampColumns(dashboard.layout?.columns ?? this.state.dashboardColumns()),
      dashboard.status === 'published' ? 'published' : 'draft',
      dashboard.publishedAt || null,
      Array.isArray(dashboard.globalFilters) ? dashboard.globalFilters : [],
      this.normalizeFiltersPosition(dashboard.globalFiltersPosition),
      Array.isArray(dashboard.tabs) ? dashboard.tabs : [],
    );
  }

  /**
   * After a chart is edited & saved, re-persist every saved dashboard that
   * references it so their stored `config` (the payload the published view reads)
   * reflects the change without the user having to reopen and re-save each one.
   * Published dashboards stay published. The currently-active dashboard is skipped
   * here because saveChart already refreshed its live draft/preview, and re-saving
   * it would require re-running the full saveDashboard flow.
   */
  async resaveDashboardsForChart(chartId: string): Promise<void> {
    const affected = this.state.savedDashboards().filter(
      (dashboard) =>
        dashboard.id !== this.state.activeSavedDashboardId() &&
        dashboard.items.some((item) => item.chartId === chartId),
    );
    if (!affected.length) return;

    const rebuilt = affected.map((dashboard) => ({
      ...dashboard,
      updatedAt: new Date().toISOString(),
      config: this.rebuildDashboardConfig(dashboard),
    }));

    try {
      const persisted = await Promise.all(
        rebuilt.map((dashboard) => firstValueFrom(this.dashboardService.saveDashboard(dashboard))),
      );
      const persistedById = new Map(persisted.map((dashboard) => [dashboard.id, dashboard]));
      const nextDashboards = this.state.savedDashboards().map(
        (dashboard) => persistedById.get(dashboard.id) ?? dashboard,
      );
      if (this.persistSavedDashboards(nextDashboards)) {
        this.state.setSavedDashboards(nextDashboards);
      }
    } catch (error) {
      // Non-fatal: the chart itself saved fine; surface a soft warning so the
      // user knows to re-save the affected dashboards manually if needed.
      this.ui.showSuccess(`Chart saved, but ${affected.length} dashboard(s) using it could not be auto-updated — reopen and save them to refresh the published view.`);
    }
  }

  consolidateSavedDashboards(
    dashboards: SavedSelfServiceDashboard[],
  ): SavedSelfServiceDashboard[] {
    const dashboardsByName = new Map<string, SavedSelfServiceDashboard>();

    dashboards.forEach((dashboard) => {
      const normalizedName = this.normalizeDashboardName(dashboard.name);
      if (!normalizedName) {
        return;
      }

      const existing = dashboardsByName.get(normalizedName);
      if (!existing) {
        dashboardsByName.set(normalizedName, {
          ...dashboard,
          items: dashboard.items.map((item) => ({ ...item })),
          config: this.buildDashboardConfigFromItems(
            dashboard.name,
            dashboard.items,
            this.clampColumns(this.state.dashboardColumns()),
            this.state.dashboardStatus(),
            this.state.dashboardPublishedAt(),
            this.state.dashboardGlobalFilters(),
            this.state.dashboardGlobalFiltersPosition(),
            Array.isArray(dashboard.tabs) ? dashboard.tabs : [],
          ),
        });
        return;
      }

      const mergedItems = this.mergeDashboardItems(existing.items, dashboard.items);
      dashboardsByName.set(normalizedName, {
        ...existing,
        updatedAt: this.pickLatestIsoDate(existing.updatedAt, dashboard.updatedAt),
        items: mergedItems,
        config: this.buildDashboardConfigFromItems(
          existing.name,
          mergedItems,
          this.clampColumns(this.state.dashboardColumns()),
          this.state.dashboardStatus(),
          this.state.dashboardPublishedAt(),
          this.state.dashboardGlobalFilters(),
          this.state.dashboardGlobalFiltersPosition(),
          Array.isArray(existing.tabs) ? existing.tabs : [],
        ),
      });
    });

    return Array.from(dashboardsByName.values());
  }

  private mergeDashboardItems(
    existingItems: DashboardDraftItem[],
    newItems: DashboardDraftItem[],
  ): DashboardDraftItem[] {
    const chartIds = new Set(existingItems.map((item) => item.chartId));
    const mergedItems = existingItems.map((item) => ({ ...item }));

    newItems.forEach((item) => {
      if (chartIds.has(item.chartId)) {
        return;
      }

      chartIds.add(item.chartId);
      mergedItems.push({ ...item });
    });

    return mergedItems;
  }

  private pickLatestIsoDate(firstDate: string, secondDate: string): string {
    return new Date(firstDate).getTime() >= new Date(secondDate).getTime()
      ? firstDate
      : secondDate;
  }

  // ── Private helpers (small pure utilities duplicated from the monolith) ──

  resolveDashboardChart(item: DashboardDraftItem): SavedSelfServiceChart | undefined {
    return this.state.savedCharts().find((chart) => chart.id === item.chartId);
  }

  private clampColumns(n: number | undefined): number {
    const value = Number(n);
    if (!Number.isFinite(value)) return 3;
    return Math.max(1, Math.min(4, Math.round(value)));
  }

  private normalizeFiltersPosition(value: GlobalFiltersPosition | string | undefined): GlobalFiltersPosition {
    return value === 'left' || value === 'right' ? value : 'top';
  }

  private clampSpan(n: number | undefined): number {
    const value = Number(n);
    if (!Number.isFinite(value)) return 1;
    return Math.max(1, Math.min(4, Math.round(value)));
  }

  private defaultSpanForChart(chart: SavedSelfServiceChart): number {
    const kind = this.state.chartTypes().find((t) => t.value === chart.chartType)?.previewKind;
    if (kind === 'kpi') return 1;
    if (kind === 'table') return 2;
    // Drill-down is a compact tree (few root rows until expanded) — a single row
    // span keeps the card tight to the content instead of leaving empty space.
    if (kind === 'generic-drill-down') return 1;
    return 1;
  }

  /**
   * Pixel height for a COLLAPSED drill-down: header + one row per distinct
   * first-level value + the total row. Sizing to the collapsed tree (rather
   * than a fixed height) removes the empty space below small trees; a cap keeps
   * large hierarchies from filling the screen (they scroll internally).
   */
  private drillDownCollapsedHeightPx(chart: SavedSelfServiceChart): number {
    const rows = chart.dataset?.rows || [];
    const columns = chart.dataset?.columns || [];
    // First non-numeric column is the top drill level (mirrors reshapeDrillDown).
    const sample: Record<string, unknown> = rows.find((r) => r && typeof r === 'object') || {};
    const firstLevel = columns.find((col) => {
      const declared = (col.type || '').toLowerCase();
      return declared
        ? !['number', 'numeric', 'integer', 'float'].includes(declared)
        : !Number.isFinite(this.dataUtils.toNumber(sample[col.name] as string | undefined));
    })?.name;

    const rootCount = firstLevel
      ? new Set(rows.map((r) => String((r as Record<string, unknown>)[firstLevel] ?? '(blank)'))).size
      : Math.min(rows.length, 8);

    const HEADER_PX = 40;   // header row
    const ROW_PX = 34;      // one data row
    const TOTAL_PX = 40;    // sticky total row
    const CHROME_PX = 8;    // small breathing room
    const bodyRows = Math.max(1, rootCount);
    const content = HEADER_PX + bodyRows * ROW_PX + TOTAL_PX + CHROME_PX;

    // The published grid's heightToRows() ADDS a fixed ~72px card-header chrome
    // to this value before rounding to grid rows — but self-service widgets have
    // NO card header, so that 72px would become empty space below the tree.
    // Pre-subtract it so the final cell lands on the actual content height.
    const GRID_CHROME_PX = 72;
    const target = content - GRID_CHROME_PX;
    // Clamp: keep a sensible floor, and cap so a huge tree scrolls internally.
    return Math.max(120, Math.min(500, target));
  }

  private spanToBootstrapClass(span: number, columns: number): string {
    const bsCols = Math.max(1, Math.min(12, Math.round((span / columns) * 12)));
    return `col-12 col-md-${Math.min(12, bsCols * 2)} col-lg-${bsCols}`;
  }

  private normalizeDashboardName(name: string): string {
    return name.trim().toLowerCase();
  }

  private createDefaultStyle(): ChartStyle {
    return {
      titleOverride: '',
      showLegend: true,
      legendPosition: 'bottom',
      showLabels: false,
      labelMode: 'value',
      palette: 'default',
      xAxisLabel: '',
      yAxisLabel: '',
      xAxisLabel2: '',
      yAxisLabel2: '',
      quadrantTopLeftLabel: '',
      quadrantTopRightLabel: '',
      quadrantBottomLeftLabel: '',
      quadrantBottomRightLabel: '',
      numberFormat: 'plain',
      numberScale: 'none',
      decimals: 0,
      numberFormat2: 'plain',
      numberScale2: 'none',
      decimals2: 0,
      lineShape: 'curved',
      comparisonValue: null,
      tableRowLimit: 50,
      tooltipEnabled: true,
      tooltipTrigger: 'auto',
      tooltipBackground: '#1e293b',
      tooltipTextColor: '#f8fafc',
      tooltipBorderColor: '#334155',
      tooltipBorderRadius: 6,
      tooltipFontSize: 12,
      tooltipAxisPointer: 'line',
      tooltipDecimals: 0,
    };
  }

  private normalizeStyle(style?: Partial<ChartStyle> | null): ChartStyle {
    return { ...this.createDefaultStyle(), ...(style || {}) };
  }

  // Carried alongside buildPublishedDataset (which it feeds the sort/topN/
  // granularity shape options consumed by dataUtils.aggregateByCategory).
  getShapeOptionsFromChart(chart: SavedSelfServiceChart): ChartShapeOptions {
    return {
      sortBy: chart.sortBy || undefined,
      sortDir: chart.sortBy ? (chart.sortDir || 'asc') : undefined,
      topN: chart.topN ?? undefined,
      topNDirection: chart.topN ? (chart.topNDirection || 'top') : undefined,
      dateGranularity: chart.dateGranularity && chart.dateGranularity !== 'none' ? chart.dateGranularity : undefined,
      tooltipColumns: chart.tooltipColumns?.length ? [...chart.tooltipColumns] : undefined,
      referenceValue: chart.referenceValue ?? undefined,
    };
  }

  // Carried alongside buildPublishedDataset's gantt branch.
  private toGanttDateString(value: number): string {
    if (Number.isFinite(value) && value >= 1900 && value <= 2100) {
      return `${Math.round(value)}-01-01`;
    }
    if (value > 1e10) {
      return new Date(value).toISOString().slice(0, 10);
    }
    if (value > 0) {
      return new Date(value * 1000).toISOString().slice(0, 10);
    }
    return `${new Date().getFullYear()}-01-01`;
  }
}






